/**
 * Le repère de retour posé par la bascule du dock vers l'assistant (#1588).
 *
 * La bascule est FORCÉE : on ne demande pas son avis à l'avocat, Hector écrit
 * et l'écran suit. Le prix de cette force est une désorientation — l'avocat
 * regardait une page de réglages ou un dossier, il se retrouve dans la
 * rédaction. Ce petit registre note l'écran QUITTÉ pour que l'assistant puisse
 * offrir un chemin de retour, au lieu de laisser l'avocat chercher son fil dans
 * l'historique du navigateur.
 *
 * Indexé par conversation, comme `brouillonComposeurRelais` : le repère ne
 * s'affiche que sur le fil qui a effectivement basculé, jamais sur un fil
 * voisin ouvert depuis la barre latérale. Rien n'est persisté : un rechargement
 * de page l'oublie, et c'est voulu — le repère décrit un geste, pas un état.
 */

import { create } from 'zustand';

/** Ce que la bascule a noté de l'écran quitté. */
export interface RetourDepuisLaBascule {
  /** `${pathname}${search}` de l'onglet AU MOMENT de la bascule ; `null` hors navigateur. */
  readonly routeDOrigine: string | null;
}

/** Le repère tel que l'écran d'arrivée le peint. */
export interface RepereDeRetour {
  readonly libelle: string;
  readonly destination: string;
}

interface RetourEnregistre extends RetourDepuisLaBascule {
  readonly conversationId: string;
}

interface RetourDepuisLaBasculeStore {
  readonly retour: RetourEnregistre | null;
  readonly preserver: (conversationId: string, routeDOrigine: string | null) => void;
  readonly oublier: (conversationId: string) => void;
  readonly toutOublier: () => void;
}

const useRetourDepuisLaBasculeStore = create<RetourDepuisLaBasculeStore>()((set) => ({
  retour: null,
  preserver: (conversationId, routeDOrigine) => set({ retour: { conversationId, routeDOrigine } }),
  oublier: (conversationId) => set((state) => (
    state.retour?.conversationId === conversationId ? { retour: null } : state
  )),
  toutOublier: () => set({ retour: null }),
}));

/**
 * Où mène le repère, et sous quel nom.
 *
 * **Le dossier de la conversation prime sur l'écran quitté.** Un fil de dossier
 * ramène au dossier, qui est le lieu de travail de l'avocat — même quand il
 * parlait à Hector depuis une page de réglages. Sans dossier, il n'existe pas
 * de meilleur but que l'écran d'où il vient. Sans l'un ni l'autre, on ne rend
 * rien : un bouton qui ne mène nulle part vaut moins que pas de bouton.
 */
export function decrireLeRetourDepuisLaBascule(entree: {
  readonly caseId: string | null;
  readonly routeDOrigine: string | null;
}): RepereDeRetour | null {
  if (entree.caseId?.trim()) {
    return { libelle: 'Revenir au dossier', destination: `/cases/${entree.caseId.trim()}` };
  }
  if (entree.routeDOrigine?.trim()) {
    return { libelle: "Revenir à l'écran précédent", destination: entree.routeDOrigine };
  }
  return null;
}

/** Note l'écran quitté pour le fil qui vient de basculer. */
export function preserverLeRetourDepuisLaBascule(conversationId: string, routeDOrigine: string | null): void {
  useRetourDepuisLaBasculeStore.getState().preserver(conversationId, routeDOrigine);
}

/** Lecture impérative (tests, gestes hors rendu). */
export function lireLeRetourDepuisLaBascule(conversationId: string | null): RetourDepuisLaBascule | null {
  const retour = useRetourDepuisLaBasculeStore.getState().retour;
  if (!conversationId || retour?.conversationId !== conversationId) return null;
  return { routeDOrigine: retour.routeDOrigine };
}

/** L'avocat s'est servi du repère : il a fait son office et disparaît. */
export function oublierLeRetourDepuisLaBascule(conversationId: string): void {
  useRetourDepuisLaBasculeStore.getState().oublier(conversationId);
}

/** Remise à zéro entre deux tests ou deux stories. */
export function oublierLesRetoursDepuisLaBascule(): void {
  useRetourDepuisLaBasculeStore.getState().toutOublier();
}

/** Le repère à peindre sur le fil actuellement rendu par `/assistant`. */
export function useRetourDepuisLaBascule(conversationId: string | null): RetourDepuisLaBascule | null {
  return useRetourDepuisLaBasculeStore((state) => {
    const retour = state.retour;
    if (!conversationId || retour?.conversationId !== conversationId) return null;
    return retour;
  });
}
